import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Sparkles, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InnerVoicePage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [isWriting, setIsWriting] = useState(false);
  const [showAutoReply, setShowAutoReply] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [userSessionId] = useState(() => {
    return localStorage.getItem('user_session_id') || '';
  });

  const moods = [
    { value: 'tired', label: 'Tired', emoji: '🌙' },
    { value: 'heavy', label: 'Heavy', emoji: '🌧️' },
    { value: 'healing', label: 'Healing', emoji: '🌿' },
    { value: 'calm', label: 'Calm', emoji: '☀️' },
    { value: 'determined', label: 'Determined', emoji: '🔥' }
  ];

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const response = await axios.get(`${API}/diary/entries/${userSessionId}`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error loading entries:', error);
      // Fallback to localStorage if API fails
      const saved = localStorage.getItem('journal_entries');
      if (saved) {
        setEntries(JSON.parse(saved));
      }
    }
  };

  const saveEntry = async () => {
    if (!currentEntry.trim()) {
      toast.error('Write something first');
      return;
    }

    setIsSaving(true);

    try {
      // Save to backend and get auto-reply
      const response = await axios.post(`${API}/diary/entries`, {
        user_session_id: userSessionId,
        text_entry: currentEntry,
        mood: selectedMood || null
      });

      // Show auto-reply modal
      setAutoReplyMessage(response.data.auto_reply);
      setShowAutoReply(true);

      // Reload entries
      await loadEntries();
      
      // Reset form
      setCurrentEntry('');
      setSelectedMood('');
      setIsWriting(false);
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMoodEmoji = (moodValue) => {
    const mood = moods.find(m => m.value === moodValue);
    return mood ? mood.emoji : '';
  };

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Auto-Reply Modal */}
      {showAutoReply && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-[#3E324D] to-[#2E2536] rounded-3xl shadow-2xl max-w-lg w-full p-8 border-2 border-[#E7C6C2]/30 animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#E7C6C2]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-[#E7C6C2]" />
              </div>
              <h3 className="text-2xl font-bold text-[#EDE8F3] mb-2">
                Thank you for sharing.
              </h3>
            </div>
            
            <div className="bg-[#2E2536] rounded-2xl p-6 mb-6">
              <p className="text-[#EDE8F3] leading-relaxed text-center italic">
                "{autoReplyMessage}"
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowAutoReply(false);
                  navigate('/journey');
                }}
                className="flex-1 bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white py-3 rounded-full"
              >
                View My Journey
              </Button>
              <Button
                onClick={() => setShowAutoReply(false)}
                className="flex-1 bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-3 rounded-full"
              >
                Back to Haven
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#E7C6C2]" />
              <h1 className="text-2xl font-bold text-[#EDE8F3]">Inner Voice</h1>
            </div>
            
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Writing Area */}
        {isWriting && (
          <div className="bg-[#3E324D] rounded-2xl p-6 mb-8 shadow-lg">
            <h3 className="text-xl font-semibold text-[#EDE8F3] mb-4">
              How are you really feeling today?
            </h3>
            
            {/* Mood Selector */}
            <div className="mb-4">
              <label className="text-[#C8B5E0] text-sm mb-3 block">Choose a mood (optional)</label>
              <div className="grid grid-cols-5 gap-2">
                {moods.map((mood) => (
                  <button
                    key={mood.value}
                    onClick={() => setSelectedMood(mood.value)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      selectedMood === mood.value
                        ? 'border-[#E7C6C2] bg-[#E7C6C2]/20 scale-105'
                        : 'border-[#C8B5E0]/20 hover:border-[#C8B5E0]/40'
                    }`}
                  >
                    <div className="text-3xl mb-1">{mood.emoji}</div>
                    <div className="text-[#EDE8F3] text-xs">{mood.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <textarea
              value={currentEntry}
              onChange={(e) => setCurrentEntry(e.target.value)}
              placeholder="Type what's been sitting on your mind…"
              className="w-full min-h-[300px] bg-[#2E2536] text-[#EDE8F3] placeholder-[#6E6275] rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-[#C8B5E0] resize-none"
              autoFocus
            />
            <div className="flex gap-3 mt-4">
              <Button
                onClick={saveEntry}
                disabled={isSaving}
                className="flex-1 bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-3 rounded-full font-semibold disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : '💫 Save & Reflect'}
              </Button>
              <Button
                onClick={() => {
                  setIsWriting(false);
                  setCurrentEntry('');
                  setSelectedMood('');
                }}
                variant="ghost"
                className="text-[#C8B5E0] hover:bg-[#C8B5E0]/10"
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* New Entry Button */}
        {!isWriting && (
          <Button
            onClick={() => setIsWriting(true)}
            className="w-full bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white py-6 rounded-xl mb-8 text-lg"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Write to Yourself
          </Button>
        )}

        {/* Entries List - Show only last 3 entries as preview */}
        {entries.length === 0 && !isWriting ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-[#C8B5E0] mx-auto mb-4 opacity-50" />
            <p className="text-[#EDE8F3] text-xl mb-2">No entries yet</p>
            <p className="text-[#C8B5E0] max-w-md mx-auto leading-relaxed">
              When you're ready, write to yourself — not to fix, but to understand.
            </p>
          </div>
        ) : entries.length > 0 && !isWriting ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#EDE8F3]">Recent Reflections</h2>
              <Button
                onClick={() => navigate('/journey')}
                variant="ghost"
                className="text-[#E7C6C2] hover:bg-[#E7C6C2]/10"
              >
                View Full Journey →
              </Button>
            </div>
            {entries.slice(0, 3).map((entry) => (
              <div 
                key={entry.id}
                className="bg-[#3E324D] rounded-xl p-6 hover:bg-[#4A3B5A] transition-colors cursor-pointer"
                onClick={() => navigate('/journey')}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-[#C8B5E0] text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(entry.created_at)}</span>
                  </div>
                  {entry.mood && (
                    <span className="text-2xl">{getMoodEmoji(entry.mood)}</span>
                  )}
                </div>
                <p className="text-[#EDE8F3] leading-relaxed whitespace-pre-wrap line-clamp-2">{entry.text_entry}</p>
              </div>
            ))}
            <div className="text-center pt-4">
              <Button
                onClick={() => navigate('/journey')}
                className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-6 py-3 rounded-full"
              >
                📖 See All {entries.length} Entries in Your Journey
              </Button>
            </div>
          </div>
        ) : null}

        {/* Footer Tip */}
        {entries.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-sm text-[#C8B5E0] italic">
              Some days, revisiting an old entry brings new meaning.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InnerVoicePage;
